import _ from 'lodash';

import {
  useCallback,
  useRef,
  useReducer,
  useMemo,
  HTMLInputTypeAttribute
} from 'react';

import { SelectChangeEvent } from '@mui/material';



export type IUpdateAction<TState extends object, TKey extends keyof TState = keyof TState> = {
  type: 'UPDATE';
  payload: {
    property: TKey;
    value: TState[TKey];
  };
};

export type IResetAction = {
  type: 'RESET';
};

export type ITrimAction = {
  type: 'TRIM';
};

export type IBatchUpdateAction<TState extends object> = {
  type: 'BATCH_UPDATE';
  payload: TState;
};

export type IAction<TState extends object> = (
  IUpdateAction<TState> |
  IResetAction |
  ITrimAction |
  IBatchUpdateAction<TState>
);



export type IFieldConfig<TState, TKeyValue> = {
  label?: string;
  required?: boolean;
  type?: HTMLInputTypeAttribute;
  fnValidate?: (value: TKeyValue, state: TState) => string;
};

export function useFormState<TState extends object, TKey extends keyof TState = keyof TState>({
  initialState, key2FieldConfig = {}
}: {
  initialState: TState;
  key2FieldConfig?: Partial<Record<TKey, IFieldConfig<TState, TState[TKey]>>>;
}) {
  const fnReducer = useCallback(
    (currentState: TState, action: IAction<TState>): TState => {
      switch (action.type) {
      // Currently only handles strings and numbers. Need to think through other value types.
      case 'UPDATE':
        if (typeof action.payload.value === typeof currentState[action.payload.property]) {
          return {
            ...currentState,
            [action.payload.property]: action.payload.value
          };
        }
        break;

      case 'TRIM':
        return trimFormStateValues(currentState);

      case 'BATCH_UPDATE':
        return { ...action.payload };

      case 'RESET':
        return {
          ...initialState
        };
      }
      return currentState;
    },
    [initialState]
  );

  const refInitialState = useRef(initialState);
  const [formState, dispatch] = useReducer(fnReducer, refInitialState.current);



  const genFieldProps = useCallback(
    <TThisKey extends keyof typeof key2FieldConfig, TValue extends TState[TThisKey]>(fieldKey: TThisKey) => {
      const defaultProps = key2FieldConfig[fieldKey] ?? {};
      const errorMessage = defaultProps.fnValidate?.(formState[fieldKey], formState) ?? '';
      const errorObj = errorMessage ? {
        error: !!errorMessage,
        helperText: errorMessage
      } : {};

      return {
        ...defaultProps,
        ...errorObj,
        value: formState[fieldKey],
        onBlur: () => dispatch({ type: 'TRIM' }),
        onChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent) => {
          if (formState[fieldKey] !== event.target.value) {
            dispatch({
              type: 'UPDATE',
              payload: {
                property: fieldKey,
                // Hack to get around type issue since we know above.
                value: event.target.value as TValue
              }
            });
          }
        }
      };
    },
    [
      key2FieldConfig,
      formState
    ]
  );



  return useMemo(
    () => ({
      formState,
      dispatch,
      genFieldProps
    }),
    [
      genFieldProps,
      formState
    ]);
}



export function trimFormStateValues<T>(formState: T): T {
  if (_.isObject(formState)) {
    return _.mapValues(formState, value => {
      if (_.isString(value)) {
        return _.trim(value);
      }

      if (_.isArray(value)) {
        return _.map(value, v => trimFormStateValues(v));
      }

      if (_.isDate(value)) {
        return value;
      }

      if (_.isObject(value)) {
        return trimFormStateValues(value);
      }

      return value;
    }) as T;
  }

  // Generally shouldn't happen but this function can recurse and be passed a string.
  if (_.isString(formState)) {
    return _.trim(formState) as T;
  }

  return formState;
}
